import { theme as antdTheme, type ThemeConfig } from 'antd';
import type { Theme } from '@mui/material/styles';

/**
 * Builds an Ant Design theme configuration from the active MUI theme so that
 * Ant Design components (used across most pages) stay visually in sync with the
 * selected professional theme. This is what lets the theme selector restyle the
 * whole application, not just the MUI-based screens.
 */
export const buildAntdTheme = (mui: Theme): ThemeConfig => {
    const isDark = mui.palette.mode === 'dark';
    const radius = Number(mui.shape.borderRadius) || 6;

    return {
        algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
        token: {
            colorPrimary: mui.palette.primary.main,
            colorInfo: mui.palette.info.main,
            colorSuccess: mui.palette.success.main,
            colorWarning: mui.palette.warning.main,
            colorError: mui.palette.error.main,
            colorLink: mui.palette.primary.main,
            colorBgLayout: mui.palette.background.default,
            colorBgContainer: mui.palette.background.paper,
            colorText: mui.palette.text.primary,
            colorTextSecondary: mui.palette.text.secondary,
            colorBorder: mui.palette.divider,
            colorBorderSecondary: mui.palette.divider,
            borderRadius: radius,
            borderRadiusLG: radius + 2,
            fontSize: 13,
            fontFamily: mui.typography.fontFamily,
            controlHeight: 30,
            wireframe: false,
        },
        components: {
            Card: {
                borderRadiusLG: radius + 2,
                boxShadowTertiary:
                    '0 1px 2px rgba(16, 24, 40, 0.04), 0 8px 24px rgba(16, 24, 40, 0.06)',
                headerFontSize: 15,
                paddingLG: 16,
            },
            Table: {
                headerBg: isDark ? undefined : '#f8fafc',
                headerColor: mui.palette.text.secondary,
                headerSplitColor: 'transparent',
                borderColor: mui.palette.divider,
                rowHoverBg: isDark ? undefined : '#f5f7fb',
                cellPaddingBlock: 8,
                cellPaddingInline: 12,
                fontSize: 13,
            },
            Tabs: {
                titleFontSize: 13,
                horizontalItemPadding: '8px 12px',
            },
            Button: {
                fontWeight: 600,
                controlHeight: 30,
                paddingContentHorizontal: 14,
            },
            Statistic: {
                titleFontSize: 13,
                contentFontSize: 22,
            },
            Segmented: {
                borderRadius: radius,
            },
        },
    };
};
